import { z } from 'zod';

export const ServiceWindowStatus = z.enum(['draft', 'published', 'canceled']);
export type ServiceWindowStatus = z.infer<typeof ServiceWindowStatus>;

/**
 * Cohort filter applied at picker time to determine which vehicles can book
 * into a given service window. Stored on the window, evaluated against each
 * vehicle when the operator picker queries `availableFor(vehicleId)`.
 *
 * All fields are optional and AND-combined — `{ vehicleType: 'transit-van' }`
 * matches any transit van; `{ vehicleType: 'transit-van', departmentId: 'X' }`
 * matches only transit vans in department X.
 */
export const VehicleCohortFilter = z.object({
  trade: z.string().max(100).optional(),
  departmentId: z.string().uuid().optional(),
  assetTag: z.string().max(100).optional(),
  vehicleType: z.string().max(100).optional(),
});
export type VehicleCohortFilter = z.infer<typeof VehicleCohortFilter>;

export const ServiceWindow = z.object({
  id: z.string().uuid(),
  windowStart: z.string().datetime(),
  windowEnd: z.string().datetime(),
  locationAddress: z.string().max(500),
  locationCoords: z
    .object({ lat: z.number(), lng: z.number() })
    .nullable()
    .optional(),
  vendorId: z.string().uuid(),
  vehicleCohort: VehicleCohortFilter,
  capacity: z.number().int().min(1).max(50),
  status: ServiceWindowStatus,
  /** When set, this window was auto-seeded from a Maximo PM rule. */
  pmSourceId: z.string().uuid().nullable().optional(),
  publishedAt: z.string().datetime().nullable().optional(),
  canceledAt: z.string().datetime().nullable().optional(),
  cancelReason: z.string().max(500).nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ServiceWindow = z.infer<typeof ServiceWindow>;

export const CreateServiceWindowInput = ServiceWindow.omit({
  id: true,
  status: true,
  publishedAt: true,
  canceledAt: true,
  cancelReason: true,
  pmSourceId: true,
  createdAt: true,
  updatedAt: true,
}).extend({
  status: ServiceWindowStatus.default('draft'),
});
export type CreateServiceWindowInput = z.infer<typeof CreateServiceWindowInput>;

export const UpdateServiceWindowInput = CreateServiceWindowInput.partial().extend({
  cancelReason: z.string().max(500).optional(),
});
export type UpdateServiceWindowInput = z.infer<typeof UpdateServiceWindowInput>;
